import React from "react";
import {
  Table,
  TableBody,
  TableContainer,
  TableHead,
  TableRow,
  useTheme,
  Theme,
  Box,
  TableSortLabel,
} from "@mui/material";
import { SortType } from "../../../models/SortType";
import AppTableHeaderCell from "../common/pages/AppTableHeaderCell";
import AppTableRowCell from "../common/pages/AppTableRowCell";
import AppTablePagenation from "../common/pages/AppTablePagenation";
import CustomTypography from "../common/form/CustomTypography";
import { observer } from "mobx-react";
import { formatDate } from "../common/functions/formatDate";
import { SwageazyInventoryDetails } from "../../../models/SwageazyInventory/SwageazyInventory";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import { useStores } from "../../../use-stores";

interface SwageazyInventoryProductsTableProps {
  data: SwageazyInventoryDetails[];
  maxHeight?: number;
  minHeight?: number;
  limit: number;
  page_no: number;
  total_pages: number;
  total_count: number;
  sortBy?: string;
  sortType?: SortType;
  handleQuantityClick?: (id: number) => void;
  onPageChange: (
    event: React.MouseEvent<HTMLButtonElement> | null,
    page: number
  ) => void;
  onRowsPerPageChange: (limit: number) => void;
  onSortChange?: (column: string, sortType: SortType) => void;
  handleViewImages: (item: SwageazyInventoryDetails) => void;
}

const SwageazyInventoryProductsTable = ({
  data,
  maxHeight,
  page_no,
  limit,
  total_count,
  sortBy,
  sortType,
  minHeight = window.innerHeight - 350,
  onPageChange,
  onRowsPerPageChange,
  onSortChange,
  handleViewImages,
  handleQuantityClick,
}: SwageazyInventoryProductsTableProps) => {
  const theme = useTheme<Theme>();
  const { swageazyInventoryStore } = useStores();

  const handleRowsChange = (value: number) => {
    onPageChange(null, 1);
    onRowsPerPageChange(value);
  };

  const renderSortIcon = (property: string) => {
    if (sortBy === property) {
      return sortType === SortType.ASC ? "↑" : "↓";
    }
    return "↑↓";
  };

  return (
    <>
      {data.length > 0 ? (
        <Box>
          <TableContainer
            sx={{
              maxHeight: maxHeight,
              minHeight: minHeight,
              overflow: "auto",
            }}
          >
            <Table stickyHeader>
              <TableHead
                sx={{
                  position: "relative",
                  zIndex: 30,
                }}
              >
                <TableRow>
                  <AppTableHeaderCell
                    sx={{
                      textAlign: "center",
                      fontSize: 14,
                      position: "sticky",
                      left: 0,
                      top: 0,
                      zIndex: 30,
                      minWidth: "90px",
                      backgroundColor: theme.palette.background.paper,
                      isolation: "isolate",
                    }}
                  >
                    <TableSortLabel
                      active={sortBy === "id"}
                      direction={sortType === SortType.ASC ? "asc" : "desc"}
                      onClick={() =>
                        onSortChange?.(
                          "id",
                          sortBy === "id" && sortType === SortType.DESC
                            ? SortType.ASC
                            : SortType.DESC
                        )
                      }
                      IconComponent={() => <span>{renderSortIcon("id")}</span>}
                      sx={{ fontSize: "12px" }}
                    >
                      PRODUCT ID
                    </TableSortLabel>
                  </AppTableHeaderCell>

                  <AppTableHeaderCell minWidth="100px">
                    <TableSortLabel
                      active={sortBy === "name"}
                      direction={sortType === SortType.ASC ? "asc" : "desc"}
                      onClick={() =>
                        onSortChange?.(
                          "name",
                          sortBy === "name" && sortType === SortType.DESC
                            ? SortType.ASC
                            : SortType.DESC
                        )
                      }
                      IconComponent={() => (
                        <span>{renderSortIcon("name")}</span>
                      )}
                      sx={{ fontSize: "12px" }}
                    >
                      Product Name
                    </TableSortLabel>
                  </AppTableHeaderCell>

                  <AppTableHeaderCell minWidth="100px">
                    HSN Code
                  </AppTableHeaderCell>
                  <AppTableHeaderCell minWidth="100px">GST%</AppTableHeaderCell>

                  <AppTableHeaderCell minWidth="150px">
                    Quantity
                  </AppTableHeaderCell>

                  <AppTableHeaderCell minWidth="120px">
                    Blocked Quantity
                  </AppTableHeaderCell>

                  <AppTableHeaderCell minWidth="120px">
                    Available Quantity
                  </AppTableHeaderCell>

                  <AppTableHeaderCell minWidth="100px">
                    Last Updated By
                  </AppTableHeaderCell>

                  <AppTableHeaderCell minWidth="150px">
                    <TableSortLabel
                      active={sortBy === "last_updated_at"}
                      direction={sortType === SortType.ASC ? "asc" : "desc"}
                      onClick={() =>
                        onSortChange?.(
                          "last_updated_at",
                          sortBy === "last_updated_at" &&
                            sortType === SortType.DESC
                            ? SortType.ASC
                            : SortType.DESC
                        )
                      }
                      IconComponent={() => (
                        <span>{renderSortIcon("last_updated_at")}</span>
                      )}
                      sx={{ fontSize: "12px" }}
                    >
                      Last Updated At
                    </TableSortLabel>
                  </AppTableHeaderCell>
                </TableRow>
              </TableHead>

              <TableBody>
                {data?.map((item: SwageazyInventoryDetails) => (
                  <TableRow key={item?.id}>
                    <AppTableRowCell
                      sx={{
                        fontSize: "12px",
                        textAlign: "center",
                        left: 0,
                        position: "sticky",
                        zIndex: 10,
                        boxShadow: "10px 0 20px -12px rgba(115,115,115,0.3)",
                        backgroundColor: theme.palette.background.paper,
                        fontWeight: 600,
                        minWidth: "60px",
                      }}
                    >
                      {item?.id}
                    </AppTableRowCell>
                    <AppTableRowCell
                      sx={{
                        minWidth: "250px",
                        maxWidth: "300px",
                        fontSize: "12px",
                        textAlign: "center",
                        fontWeight: 600,
                        whiteSpace: "normal",
                        wordWrap: "break-word",
                        overflowWrap: "anywhere",
                      }}
                    >
                      <Box display="flex" alignItems="center">
                        <Box
                          sx={{
                            position: "relative",
                            background: "rgba(242, 242, 242, 1)",
                            padding: 1,
                            display: "flex",
                            justifyContent: "center",
                            alignItems: "center",
                            marginRight: 2,
                            minHeight: "55px",
                            maxHeight: "55px",
                            borderRadius: "4px",
                          }}
                          onMouseEnter={() =>
                            swageazyInventoryStore.setHoverIndex(item?.id)
                          }
                          onMouseLeave={() =>
                            swageazyInventoryStore.setHoverIndex(null)
                          }
                        >
                          <img
                            style={{
                              maxHeight: "38px",
                              minHeight: "38px",
                              maxWidth: "38px",
                              minWidth: "38px",
                            }}
                            src={item?.images?.[0]}
                            alt={item?.name}
                          />
                          {swageazyInventoryStore.hoverIndex === item?.id && (
                            <Box
                              className="delete-icon-2"
                              sx={{
                                position: "absolute",
                                top: 0,
                                left: 0,
                                width: "100%",
                                height: "100%",
                                backgroundColor:
                                  theme.palette.customColors.backdrop,
                                display: "flex",
                                justifyContent: "center",
                                gap: "10px",
                                alignItems: "center",
                                opacity: 1,
                                transition: "opacity 0.3s ease-in-out",
                                zIndex: 1000,
                              }}
                            >
                              <VisibilityOutlinedIcon
                                sx={{
                                  color: theme.palette.customColors.white,
                                  cursor: "pointer",
                                  backgroundColor:
                                    theme.palette.customColors.backdrop,
                                  borderRadius: "50%",
                                  padding: "4px",
                                  height: 20,
                                  width: 20,
                                }}
                                onClick={() => handleViewImages(item)}
                              />
                            </Box>
                          )}
                        </Box>
                        <Box sx={{ textAlign: "left" }}>
                          <CustomTypography
                            variant="body2"
                            fontWeight={700}
                            title={item?.name}
                            sx={{
                              cursor: "default",
                            }}
                          >
                            {item?.name}
                          </CustomTypography>
                        </Box>
                      </Box>
                    </AppTableRowCell>
                    <AppTableRowCell>{item?.hsn_code}</AppTableRowCell>
                    <AppTableRowCell>{item?.gst_percentage}</AppTableRowCell>
                    <AppTableRowCell
                      onClick={() => handleQuantityClick?.(item?.id)}
                      sx={{
                        cursor: "pointer",
                        textDecoration: "underline",
                        color: theme.palette.blue.main,
                        fontWeight: 700,
                      }}
                    >
                      {item?.quantity}
                    </AppTableRowCell>
                    <AppTableRowCell>{item?.blocked_quantity}</AppTableRowCell>
                    <AppTableRowCell>
                      {item?.quantity - (item?.blocked_quantity || 0) || 0}
                    </AppTableRowCell>
                    <AppTableRowCell>{item?.last_updated_by}</AppTableRowCell>
                    <AppTableRowCell>
                      {formatDate(item?.last_updated_at)}
                    </AppTableRowCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
          <AppTablePagenation
            fontSize="12px"
            pageNo={page_no}
            limit={limit}
            totalCount={total_count}
            onPageChange={onPageChange}
            handleRowsChange={handleRowsChange}
          />
        </Box>
      ) : (
        <Box
          sx={{
            display: "flex",
            minHeight: "100px",
            paddingTop: 5,
            justifyContent: "center",
            alignItems: "center",
          }}
        >
          <CustomTypography
            color={theme?.palette?.customColors?.greyTextColor}
            variant="heading8"
            fontWeight={700}
          >
            NO PRODUCT FOUND!
          </CustomTypography>
        </Box>
      )}
    </>
  );
};

export default observer(SwageazyInventoryProductsTable);
